import React, { useContext, useCallback, useEffect, useState } from "react";
import { InterviewContext } from "src/contexts/InterviewContext";

interface SkipThisQuestionProps {
  isActive?: boolean;
  onClick?: () => void | Promise<void>;
}

const SkipThisQuestionButton: React.FC<SkipThisQuestionProps> = ({
  isActive = true,
  onClick,
}) => {
  const { finishAnsweringHandler, isInterviewerSpeaking } =
    useContext(InterviewContext)!;
  const [isProcessing, setIsProcessing] = useState(false);

  // Simplified click handler - no debounce, direct execution
  const handleSkipThisQuestionClick = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      // Prevent any default behavior
      e.preventDefault();

      console.log("Skip button clicked!");

      // Prevent multiple clicks
      if (isProcessing) {
        console.log("Already processing skip request");
        return;
      }

      // Prevent clicking while interviewer is speaking
      if (isInterviewerSpeaking) {
        console.log("Interviewer is speaking. Please listen.");
        return;
      }

      if (!isActive) {
        console.log("Button is not active");
        return;
      }

      try {
        setIsProcessing(true);

        // Execute the onClick handler if provided
        if (onClick) {
          console.log("Executing onClick handler");
          await onClick();
        }

        // Call the finishAnsweringHandler with true to skip
        console.log("Calling finishAnsweringHandler(true)");
        await finishAnsweringHandler(true);

        console.log("Skip question process completed");
      } catch (error) {
        console.error("Error in skip question handler:", error);
      } finally {
        // Reset the processing state after a delay
        setTimeout(() => {
          setIsProcessing(false);
        }, 2000);
      }
    },
    [
      isActive,
      finishAnsweringHandler,
      onClick,
      isProcessing,
      isInterviewerSpeaking,
    ]
  );

  // Stop any ongoing processes if component unmounts
  useEffect(() => {
    return () => {
      setIsProcessing(false);
    };
  }, []);

  const buttonClass =
    isActive && !isProcessing && !isInterviewerSpeaking
      ? "flex items-center justify-center relative px-4 py-2 bg-[#fff] rounded-[8px] border-[1px] border-[#D4D6DB] text-[1rem]"
      : "flex items-center justify-center relative text-[#b8bbc2] bg-[#f1f2f3] rounded-[8px] px-4 py-2 text-[1rem]";

  const iconColor =
    isActive && !isProcessing && !isInterviewerSpeaking ? "#444444" : "#B8BBC2";

  return (
    <button
      onClick={handleSkipThisQuestionClick}
      disabled={isProcessing || isInterviewerSpeaking || !isActive}
      aria-label={
        isActive ? "Skip This Question" : "Skip This Question (Inactive)"
      }
      className={buttonClass}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="17"
        height="20"
        viewBox="0 0 17 20"
        fill="none"
      >
        <path d="M4 2 L4 18 L13 10 Z" fill={iconColor} />
      </svg>
      <div className="ml-2">{isProcessing ? "Skipping..." : "Skip"}</div>
    </button>
  );
};

export default SkipThisQuestionButton;
